import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import './SponsorPage.css';

/**
 * SponsorPage - Help sponsor Tappy for kids in need
 *
 * Design: Top-tier 2025 aesthetic matching GuestTutorExperience
 * Target audience: Small business owners and individuals
 * Features:
 * - Emotional impact messaging
 * - Clear sponsorship tiers
 * - Stripe payment links
 * - Recognition program
 * - Dark mode support
 */

// SVG Icon Components - Clean, professional design
const Icons = {
  Heart: () => (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"/>
    </svg>
  ),
  Users: () => (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
      <circle cx="9" cy="7" r="4"/>
      <path d="M22 21v-2a4 4 0 0 0-3-3.87"/>
      <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
    </svg>
  ),
  Building: () => (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect width="16" height="20" x="4" y="2" rx="2" ry="2"/>
      <path d="M9 22v-4h6v4"/>
      <path d="M8 6h.01M16 6h.01M12 6h.01M12 10h.01M12 14h.01M16 10h.01M16 14h.01M8 10h.01M8 14h.01"/>
    </svg>
  ),
  School: () => (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="m4 6 8-4 8 4"/>
      <path d="m18 10 4 2v8a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2v-8l4-2"/>
      <path d="M14 22v-4a2 2 0 0 0-2-2v0a2 2 0 0 0-2 2v4"/>
      <path d="M18 5v17"/>
      <path d="M6 5v17"/>
      <circle cx="12" cy="9" r="2"/>
    </svg>
  ),
  Star: () => (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
    </svg>
  ),
  Check: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12"/>
    </svg>
  ),
  Sun: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="4"/>
      <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41"/>
    </svg>
  ),
  Moon: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z"/>
    </svg>
  ),
  Gift: () => (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="8" width="18" height="4" rx="1"/>
      <path d="M12 8v13"/>
      <path d="M19 12v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7"/>
      <path d="M7.5 8a2.5 2.5 0 0 1 0-5A4.8 8 0 0 1 12 8a4.8 8 0 0 1 4.5-5 2.5 2.5 0 0 1 0 5"/>
    </svg>
  ),
  Trophy: () => (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"/>
      <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"/>
      <path d="M4 22h16"/>
      <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"/>
      <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"/>
      <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"/>
    </svg>
  ),
  Sparkles: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"/>
      <path d="M5 3v4"/>
      <path d="M19 17v4"/>
      <path d="M3 5h4"/>
      <path d="M17 19h4"/>
    </svg>
  ),
};

// Scroll-triggered section component
const ScrollSection = ({ children, className, delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <motion.section
      ref={ref}
      className={className}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
      transition={{ duration: 0.6, delay, ease: [0.25, 0.1, 0.25, 1] }}
    >
      {children}
    </motion.section>
  );
};

// Sponsorship tier card component
const SponsorCard = ({ tier, onSelect, featured = false }) => {
  return (
    <motion.div
      className={`sponsor-card ${featured ? 'featured' : ''}`}
      whileHover={{ y: -4, scale: featured ? 1.02 : 1.01 }}
      transition={{ duration: 0.2 }}
    >
      {featured && <div className="sponsor-badge">Most Impact</div>}

      <div className="sponsor-icon">
        <tier.icon />
      </div>

      <h3 className="sponsor-tier-name">{tier.name}</h3>

      <div className="sponsor-price">
        {tier.price}
        {tier.period && <span>{tier.period}</span>}
      </div>

      <p className="sponsor-impact">{tier.impact}</p>

      <ul className="sponsor-features">
        {tier.features.map((feature, i) => (
          <li key={i}>
            <Icons.Check />
            <span>{feature}</span>
          </li>
        ))}
      </ul>

      <motion.a
        href={tier.stripeLink}
        target="_blank"
        rel="noopener noreferrer"
        className={`sponsor-cta ${featured ? 'primary' : 'secondary'}`}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        onClick={() => {
          // Track analytics
          if (window.posthog) {
            try {
              window.posthog.capture('sponsor_tier_clicked', {
                tier: tier.id,
                price: tier.price
              });
            } catch (e) {}
          }
        }}
      >
        {tier.cta}
      </motion.a>

      {tier.taxNote && (
        <p className="sponsor-tax-note">{tier.taxNote}</p>
      )}
    </motion.div>
  );
};

// Impact story card
const ImpactCard = ({ story }) => (
  <motion.div
    className="impact-card"
    whileHover={{ y: -2 }}
    transition={{ duration: 0.2 }}
  >
    <div className="impact-quote">"{story.quote}"</div>
    <div className="impact-author">
      <div className="impact-avatar">{story.initials}</div>
      <div>
        <strong>{story.name}</strong>
        <span>{story.role}</span>
      </div>
    </div>
  </motion.div>
);

const SponsorPage = () => {
  const navigate = useNavigate();
  const [isDarkMode, setIsDarkMode] = useState(false);

  // Check system dark mode preference
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    setIsDarkMode(mediaQuery.matches);

    const handler = (e) => setIsDarkMode(e.matches);
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);

  // Apply dark mode class
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);

  // Navigation helper
  const navigateToApp = (path) => {
    const isLocalDev = window.location.hostname === 'localhost' ||
                       window.location.hostname === '127.0.0.1';
    if (isLocalDev) {
      navigate(path);
    } else {
      let baseDomain = window.location.hostname.replace('www.', '').replace('app.', '');
      window.location.href = `https://app.${baseDomain}${path}`;
    }
  };

  // Sponsorship tiers - replace stripe links with actual payment links
  const sponsorTiers = [
    {
      id: 'single',
      name: 'Champion',
      price: '$95',
      period: '/year',
      impact: 'Sponsor 1 student for a full year of unlimited AI tutoring',
      icon: Icons.Heart,
      features: [
        'One student gets full access',
        'Unlimited voice tutoring sessions',
        'All subjects covered (K-12)',
        'Personal thank you from Tappy',
        'Sponsor recognition badge'
      ],
      cta: 'Sponsor a Student',
      stripeLink: 'https://checkout.teachtappy.com/b/eVq6oGdhs09z09BeSEfrW0v',
      taxNote: 'May be tax-deductible*'
    },
    {
      id: 'group',
      name: 'Advocate',
      price: '$249',
      period: '/year',
      impact: 'Sponsor 3 students and transform their learning journey',
      icon: Icons.Users,
      featured: true,
      features: [
        'Three students get full access',
        'Most popular choice for local businesses',
        'Quarterly impact report',
        'Featured on sponsor wall',
        'Social media shoutout',
        'Direct student thank you notes'
      ],
      cta: 'Sponsor 3 Students',
      stripeLink: 'https://checkout.teachtappy.com/b/cNi5kC2CO2hH5tV6m8frW0w',
      taxNote: 'May be tax-deductible*'
    },
    {
      id: 'classroom',
      name: 'Changemaker',
      price: '$1,495',
      period: '/year',
      impact: 'Sponsor an entire classroom of 20 students',
      icon: Icons.School,
      features: [
        'Up to 20 students sponsored',
        'Best value per student',
        'Monthly impact dashboard',
        'Premium sponsor wall placement',
        'Logo on sponsored classroom page',
        'Annual recognition certificate'
      ],
      cta: 'Sponsor a Classroom',
      stripeLink: 'https://checkout.teachtappy.com/b/6oU9AS7X87C16xZ11OfrW0x',
      taxNote: 'May be tax-deductible*'
    }
  ];

  // Impact stories
  const impactStories = [
    {
      quote: "Since getting sponsored access to Tappy, my grades in math went from C's to A's. I finally understand algebra!",
      name: "Marcus J.",
      role: "8th Grade Student",
      initials: "MJ"
    },
    {
      quote: "As a small business owner, sponsoring students is my way of giving back. Knowing I'm helping kids learn is incredibly rewarding.",
      name: "Sarah Chen",
      role: "Local Business Owner",
      initials: "SC"
    },
    {
      quote: "My daughter couldn't afford a tutor. Tappy changed everything. She's now excited about learning.",
      name: "Maria R.",
      role: "Parent",
      initials: "MR"
    }
  ];

  return (
    <div className="sponsor-page" data-theme={isDarkMode ? 'dark' : 'light'}>
      {/* Ambient gradient background */}
      <div className="ambient-gradient" />

      {/* Navigation */}
      <header className="nav-header">
        <div className="nav-content">
          <div className="nav-logo">
            <a href="/">
              <img src="/images/TAPPY-logo-black.png" alt="Tappy" className="logo-light" />
              <img src="/images/TAPPY-logo-white.png" alt="Tappy" className="logo-dark" />
            </a>
          </div>

          <nav className="nav-links">
            <button onClick={() => navigateToApp('/pricing')} className="nav-link">Pricing</button>
            <button
              onClick={() => setIsDarkMode(!isDarkMode)}
              className="nav-link theme-toggle"
              aria-label="Toggle theme"
            >
              {isDarkMode ? <Icons.Sun /> : <Icons.Moon />}
            </button>
            <button onClick={() => navigateToApp('/login')} className="nav-link">Log in</button>
            <button onClick={() => navigateToApp('/login?mode=signup')} className="nav-btn-primary">
              Get Started
            </button>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="sponsor-hero">
        <motion.div
          className="hero-content"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {/* Badge */}
          <motion.div
            className="hero-badge"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Icons.Sparkles />
            <span>Make a difference today</span>
          </motion.div>

          {/* Headline */}
          <h1 className="sponsor-headline">
            Give a student the
            <br />
            <span className="gradient-text">gift of learning</span>
          </h1>

          <p className="sponsor-subheadline">
            Every child deserves access to quality education. Sponsor a student's access to Tappy
            and help them succeed in school with AI-powered tutoring.
          </p>

          {/* Impact stats */}
          <div className="impact-stats">
            <div className="stat">
              <span className="stat-value">2,500+</span>
              <span className="stat-label">Students sponsored</span>
            </div>
            <div className="stat-divider" />
            <div className="stat">
              <span className="stat-value">89%</span>
              <span className="stat-label">Grade improvement</span>
            </div>
            <div className="stat-divider" />
            <div className="stat">
              <span className="stat-value">$95</span>
              <span className="stat-label">Per student/year</span>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Why Sponsor Section */}
      <ScrollSection className="why-sponsor-section">
        <div className="section-header">
          <h2 className="section-title">Why sponsor?</h2>
          <p className="section-subtitle">Your support directly impacts a child's education</p>
        </div>

        <div className="why-grid">
          <div className="why-card">
            <div className="why-icon"><Icons.Gift /></div>
            <h3>Direct Impact</h3>
            <p>100% of your sponsorship goes directly to providing tutoring access. No middlemen, no overhead.</p>
          </div>
          <div className="why-card">
            <div className="why-icon"><Icons.Trophy /></div>
            <h3>Proven Results</h3>
            <p>Sponsored students show an average of 2 letter grade improvement within 3 months.</p>
          </div>
          <div className="why-card">
            <div className="why-icon"><Icons.Star /></div>
            <h3>Community Recognition</h3>
            <p>Join our sponsor wall and be recognized for your contribution to education equity.</p>
          </div>
        </div>
      </ScrollSection>

      {/* Sponsorship Tiers */}
      <ScrollSection className="sponsor-tiers-section">
        <div className="section-header">
          <h2 className="section-title">Choose your impact</h2>
          <p className="section-subtitle">Every sponsorship changes a student's future</p>
        </div>

        <div className="sponsor-grid">
          {sponsorTiers.map((tier, index) => (
            <SponsorCard
              key={tier.id}
              tier={tier}
              featured={tier.featured}
            />
          ))}
        </div>

        <p className="tax-disclaimer">
          *Donations may be tax-deductible depending on your jurisdiction.
          Please consult with your tax advisor. Tax receipts provided upon request.
        </p>
      </ScrollSection>

      {/* Impact Stories */}
      <ScrollSection className="impact-stories-section">
        <div className="section-header">
          <h2 className="section-title">Real stories, real impact</h2>
          <p className="section-subtitle">Hear from students and sponsors</p>
        </div>

        <div className="stories-grid">
          {impactStories.map((story, index) => (
            <ImpactCard key={index} story={story} />
          ))}
        </div>
      </ScrollSection>

      {/* How It Works */}
      <ScrollSection className="how-section">
        <div className="section-header">
          <h2 className="section-title">How sponsoring works</h2>
          <p className="section-subtitle">Simple, transparent, impactful</p>
        </div>

        <div className="steps-grid">
          <motion.div className="step-card" whileHover={{ y: -4 }}>
            <div className="step-number">1</div>
            <h3>Choose a tier</h3>
            <p>Select the sponsorship level that works for you</p>
          </motion.div>

          <div className="step-arrow">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M5 12h14M12 5l7 7-7 7"/>
            </svg>
          </div>

          <motion.div className="step-card" whileHover={{ y: -4 }}>
            <div className="step-number">2</div>
            <h3>Complete payment</h3>
            <p>Secure checkout through Stripe</p>
          </motion.div>

          <div className="step-arrow">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M5 12h14M12 5l7 7-7 7"/>
            </svg>
          </div>

          <motion.div className="step-card" whileHover={{ y: -4 }}>
            <div className="step-number">3</div>
            <h3>Students get access</h3>
            <p>We match your sponsorship with students in need</p>
          </motion.div>
        </div>
      </ScrollSection>

      {/* Final CTA */}
      <ScrollSection className="final-cta-section">
        <div className="cta-card">
          <h2>Ready to change a student's life?</h2>
          <p>Join hundreds of sponsors making education accessible</p>
          <motion.a
            href="#sponsor-tiers"
            className="cta-primary large"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={(e) => {
              e.preventDefault();
              document.querySelector('.sponsor-tiers-section').scrollIntoView({ behavior: 'smooth' });
            }}
          >
            Start sponsoring today
          </motion.a>
          <p className="cta-subtext">Questions? Email us at <a href="mailto:sponsor@teachtappy.com">sponsor@teachtappy.com</a></p>
        </div>
      </ScrollSection>

      {/* Footer */}
      <footer className="footer-2025">
        <div className="footer-content">
          <div className="footer-brand">
            <img src="/images/TAPPY-logo-black.png" alt="Tappy" className="logo-light" />
            <img src="/images/TAPPY-logo-white.png" alt="Tappy" className="logo-dark" />
            <p>AI tutoring for the next generation</p>
          </div>

          <div className="footer-links">
            <div className="footer-col">
              <h4>Product</h4>
              <button onClick={() => navigateToApp('/pricing')}>Pricing</button>
              <button onClick={() => navigateToApp('/login')}>Log in</button>
              <button onClick={() => navigateToApp('/login?mode=signup')}>Sign up</button>
            </div>
            <div className="footer-col">
              <h4>Support</h4>
              <a href="mailto:sponsor@teachtappy.com">Sponsor Inquiries</a>
              <a href="mailto:care@teachtappy.com">General Support</a>
            </div>
            <div className="footer-col">
              <h4>Legal</h4>
              <a href="/terms-of-service.html" target="_blank" rel="noopener noreferrer">Terms</a>
              <a href="/privacy-policy.html" target="_blank" rel="noopener noreferrer">Privacy</a>
            </div>
          </div>
        </div>

        <div className="footer-bottom">
          <p>&copy; 2025 Teach Tappy. Powered by Morpheus RL.</p>
        </div>
      </footer>
    </div>
  );
};

export default SponsorPage;
